import {
  filterByRoles,
  DefaultLayout,
  useAuthSSO,
  LoadingOverlay,
  Icon,
  Button,
} from '@vallourec-ui/vallourec-ui'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  NavigateOptions,
  Outlet,
  To,
  useLocation,
  useNavigate,
} from 'react-router-dom'

import { useApp } from '@/stores/AppStore'

import MovingText from '@/components/MovingText'

import Notifications from '@/components/Notifications'
import StyledLoadingOverlay from '@/components/StyledLoadingOverlay'
import { getSideMenuSettings } from '@/menu/getSideMenuSettings'
import { LANGUAGES } from '@/utils/constants'

const PrivateRoute = () => {
  const {
    user: cognitoUser,
    hasSession,
    signOut,
    isAuthenticated,
  } = useAuthSSO()
  const location = useLocation()
  const { t } = useTranslation()
  const { isLoading, setIsLoading, user: userTPI, setTempLocation } = useApp()
  const navigate = useNavigate()

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [menuItems, setMenuItems] = useState<{ itemGroups: any[] }>({
    itemGroups: [],
  })

  const [isOpen, setIsOpen] = useState<boolean>(false)
  const [navigateOptions, setNavigateOptions] = useState<{
    to: To
    options?: NavigateOptions
  }>({
    to: '/',
  })

  const { pageTitle, pageSubTitle, appTitle, pageActions } = useApp()

  const navigateTo = (to: To, options?: NavigateOptions) => {
    navigate(to, options)
  }

  const initMenu = () => {
    const user = {
      exp: 1,
      id: (userTPI?.userId ?? '1') as string,
      email: userTPI?.email || cognitoUser?.email,
      name: userTPI?.userName || cognitoUser?.givenName || 'N',
      isAdmin: userTPI?.isAdmin,
      roles: ['admin'],
    }

    const menu = getSideMenuSettings({
      user,
      redirectTo: (path: string) => () => {
        return navigateTo(path)
      },
      translate: t,
    })

    const menuI = filterByRoles({ menu, user })
    setMenuItems(menuI)
  }

  const onSignOut = () => {
    setIsLoading(true)
    signOut()
    setIsLoading(false)
  }

  const userOptions = [
    {
      key: 'version',
      label: `Version ${__APP_VERSION__}`,
      onClick: () => null,
    },
    {
      key: 'roles',
      label: 'Roles: Admin, Operator',
      onClick: () => null,
    },
    {
      key: 'sign-out',
      label: t<string>('Sign Out'),
      onClick: onSignOut,
    },
  ]

  const handleLoadingScreen = useCallback(() => {
    if (isLoading) return <StyledLoadingOverlay />
  }, [isLoading])

  const init = async () => {
    const response = await hasSession()
    if (!response) {
      navigate('/login')
    }
  }

  useEffect(() => {
    initMenu()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    init()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated])

  useEffect(() => {
    initMenu()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userTPI, t, location])

  const stage = import.meta.env.VITE_REACT_APP_STAGE

  return (
    <>
      {stage !== 'prod' ? (
        <MovingText
          duration="10s"
          text={`${stage?.toUpperCase()} Environment`}
        />
      ) : (
        ''
      )}
      <DefaultLayout
        appName={appTitle}
        appLogo={null}
        pageTitle={pageTitle}
        pageActions={pageActions}
        pageSubtitle={pageSubTitle}
        userName={userTPI?.userName || cognitoUser?.givenName || 'N'}
        menuSetttings={menuItems}
        userOptions={userOptions}
        pathName={location.pathname}
        navigate={navigateTo}
        applicationLanguages={LANGUAGES}
        gap={8}
        hideLanguageSelection={false}
        disableFooter
        // disableHeader
        scrollY
        hasBreadCrumb={false}
        paddingSection="0.1rem"
        flexBody="1 0 auto"
        maxHeightBody={`calc(100vh - ${stage !== 'prod' ? '100px' : '60px'})`}
        webkitScrollbarBody={false}
        scrollbarWidthBody="thin"
        extraHeaderContent={
          <div style={{ display: 'flex', paddingRight: '1rem' }}>
            <Notifications />
          </div>
        }
      >
        {handleLoadingScreen()}
        <Outlet />
        {/* <Notifications /> */}
      </DefaultLayout>
    </>
  )
}

export default PrivateRoute
